import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import CustomContainer from "../../components/CustomContainer";
import PageHeader from "../../components/PageHeader";
import AppCard from "../../components/ui/AppCard";
import { Typography, Box, Grid, Avatar, TextField, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { FaFilePdf, FaPaperPlane } from "react-icons/fa6";
import { requestCreationCustomer, validateRegisterToken } from "../../helpers/httpRequests/customer";
import appToast from "../../components/appToast";
import { TextMaskCEP, TextMaskCNPJ, TextMaskPhone } from "../../components/MaskedInputs";

export default function CustomerRegister() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [cnpjCertificate, setCnpjCertificate] = useState(null);
    const [addressProof, setAddressProof] = useState(null);
    const [avatar, setAvatar] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [tokenStatus, setTokenStatus] = useState("checking"); // checking | valid | invalid
    const [formData, setFormData] = useState({
        name: '',
        cnpj: '',
        state_registration: '',
        social_reason: '',
        whatsapp_number: '',
        email: '',
        main_manager: '',
        main_manager_contact: '',
        financial_manager: '',
        financial_manager_contact: '',
        zip_code: '',
        address: '',
        address_number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
    });

    const estados = [
        { value: "AC", label: "ACRE" },
        { value: "AL", label: "ALAGOAS" },
        { value: "AP", label: "AMAPÁ" },
        { value: "AM", label: "AMAZONAS" },
        { value: "BA", label: "BAHIA" },
        { value: "CE", label: "CEARÁ" },
        { value: "ES", label: "ESPÍRITO SANTO" },
        { value: "GO", label: "GOIÁS" },
        { value: "MA", label: "MARANHÃO" },
        { value: "MT", label: "MATO GROSSO" },
        { value: "MS", label: "MATO GROSSO DO SUL" },
        { value: "MG", label: "MINAS GERAIS" },
        { value: "PA", label: "PARÁ" },
        { value: "PB", label: "PARAÍBA" },
        { value: "PE", label: "PERNAMBUCO" },
        { value: "PI", label: "PIAUÍ" },
        { value: "RJ", label: "RIO DE JANEIRO" },
        { value: "RN", label: "RIO GRANDE DO NORTE" },
        { value: "RS", label: "RIO GRANDE DO SUL" },
        { value: "RO", label: "RONDÔNIA" },
        { value: "RR", label: "RORAIMA" },
        { value: "SC", label: "SANTA CATARINA" },
        { value: "SP", label: "SÃO PAULO" },
        { value: "SE", label: "SERGIPE" },
        { value: "TO", label: "TOCANTINS" },
        { value: "DF", label: "DISTRITO FEDERAL" }
    ];

    // Função para atualizar campos do formData
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Buscar endereço pelo CEP
    const fetchAddressByCep = async (cep) => {
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, '')}/json/`);
            const data = await response.json();
            if (!data.erro) {
                setFormData((prev) => ({
                    ...prev,
                    address: data.logradouro || '',
                    neighborhood: data.bairro || '',
                    city: data.localidade || '',
                    state: data.uf || '',
                }));
            } else {
                appToast.error('CEP não encontrado.');
            }
        } catch (err) {
            appToast.error('Erro ao buscar CEP.');
        }
    };

    // Buscar dados da empresa pelo CNPJ
    const fetchCompanyByCnpj = async (cnpj) => {
        try {
            const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj.replace(/\D/g, '')}`);
            if (!response.ok) throw new Error();
            const data = await response.json();
            setFormData((prev) => ({
                ...prev,
                name: data.razao_social || '',
                social_reason: data.razao_social || '',
                address: data.logradouro || '',
                address_number: data.numero || '',
                complement: data.complemento || '',
                whatsapp_number: data.ddd_telefone_1 || '',
                neighborhood: data.bairro || '',
                city: data.municipio || '',
                state: data.uf || '',
                zip_code: data.cep || '',
            }));
        } catch (err) {
            appToast.error('CNPJ não encontrado ou inválido.');
        }
    };

    useEffect(() => {
        let active = true;

        async function checkToken() {
            if (!token) {
                if (active) setTokenStatus("invalid");
                appToast.error('Link de cadastro inválido ou expirado. Entre em contato com o administrador.');
                navigate('/');
                return;
            }

            try {
                const data = await validateRegisterToken(token);
                if (!active) return;

                if (data?.valid) {
                    setTokenStatus("valid");
                } else {
                    setTokenStatus("invalid");
                    appToast.error('Link de cadastro inválido ou expirado. Entre em contato com o administrador.');
                    navigate('/');
                }
            } catch (error) {
                if (!active) return;
                setTokenStatus("invalid");
                appToast.error('Link de cadastro inválido ou expirado. Entre em contato com o administrador.');
                navigate('/');
            }
        }

        checkToken();

        return () => {
            active = false;
        };
    }, [token, navigate]);

    const handleFileChange = (e, setFile) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFile({
                    name: file.name,
                    base64: reader.result
                });
            };
            reader.readAsDataURL(file);
        };
    };

    const handleAvatarChange = (e) => {

        const file = e.target.files[0];

        if (file) {

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const size = 128;
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');
                    const aspectRatio = img.width / img.height;
                    let drawWidth, drawHeight, offsetX, offsetY;

                    if (aspectRatio > 1) {
                        drawWidth = size * aspectRatio;
                        drawHeight = size;
                        offsetX = -(drawWidth - size) / 2;
                        offsetY = 0;
                    } else {
                        drawWidth = size;
                        drawHeight = size / aspectRatio;
                        offsetX = 0;
                        offsetY = -(drawHeight - size) / 2;
                    };

                    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                    const base64Image = canvas.toDataURL('image/jpeg');
                    setAvatar(base64Image);
                };

                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!cnpjCertificate && !addressProof) {
            appToast.error("Para realizar sua solicitação, é necessário incluir ambos documentos no formulário.");
            return;
        };
        const payload = {
            ...formData,
            register_token: token,
            cnpjCertificate: cnpjCertificate?.base64,
            addressProof: addressProof?.base64,
            image: avatar
        };
        setIsLoading(true);
        try {
            await requestCreationCustomer(payload);
            appToast.success('Solicitação de cadastro realizada com sucesso! Aguarde nosso contato.')
            setIsLoading(false);
            navigate('/');
        } catch (error) {
            if (error.response?.data?.message) {
                appToast.error(error.response.data.message);
            } else {
                appToast.error('Ocorreu um erro ao solicitar cadastro. Entre em contato com o suporte.');
            };
            setIsLoading(false);
        };
    };

    if (tokenStatus !== "valid") {
        return (
            <CustomContainer>
                <Box sx={{ my: '2rem' }}>
                    <PageHeader title="Cadastro de cliente" crumbs={["Cadastro"]} />
                    <AppCard>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                            {tokenStatus === "checking" ? (
                                <>
                                    <CircularProgress />
                                    <Typography>Validando link de cadastro...</Typography>
                                </>
                            ) : (
                                <Typography>Link de cadastro inválido ou expirado.</Typography>
                            )}
                        </Box>
                    </AppCard>
                </Box>
            </CustomContainer>
        );
    };

    return (
        <CustomContainer>
            <Box sx={{ my: '2rem' }}>
                <PageHeader title="Cadastro de cliente" crumbs={["Cadastro"]} />
                <AppCard>
                    <Box component="form" onSubmit={handleSubmit} autoComplete="off">
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <Typography textAlign={'left'} variant="h4">
                                    Dados da Empresa
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }} display={'flex'} >
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}>
                                    <Avatar
                                        sx={{ width: 128, height: 128, mb: '1rem' }}
                                        src={avatar}
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        id="upload-avatar"
                                        style={{ display: 'none' }}
                                        onChange={handleAvatarChange}
                                    />
                                    <label htmlFor="upload-avatar">
                                        <Button variant="contained" component="span">
                                            {avatar ? "Alterar Avatar" : "Enviar Avatar"}
                                        </Button>
                                    </label>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    placeholder="99.999.999/9999-99"
                                    required
                                    fullWidth
                                    label="CNPJ"
                                    name="cnpj"
                                    value={formData.cnpj}
                                    onChange={handleInputChange}
                                    onBlur={() => formData.cnpj.replace(/\D/g, '').length === 14 && fetchCompanyByCnpj(formData.cnpj)}
                                    InputProps={{
                                        inputComponent: TextMaskCNPJ,
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Nome"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Inscrição Estadual"
                                    name="state_registration"
                                    value={formData.state_registration}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Razão Social"
                                    name="social_reason"
                                    value={formData.social_reason}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    placeholder="(99) 9 9999-9999"
                                    required
                                    fullWidth
                                    label="Número de Whatsapp"
                                    name="whatsapp_number"
                                    value={formData.whatsapp_number}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        inputComponent: TextMaskPhone,
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="E-mail Institucional"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography textAlign={'left'} variant="h4">
                                    Dados do Responsável Geral
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Nome Completo"
                                    name="main_manager"
                                    value={formData.main_manager}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    placeholder="(99) 9 9999-9999"
                                    required
                                    fullWidth
                                    label="Número de Whatsapp"
                                    name="main_manager_contact"
                                    value={formData.main_manager_contact}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        inputComponent: TextMaskPhone,
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography textAlign={'left'} variant="h4">
                                    Dados do Responsável Financeiro
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Nome Completo"
                                    name="financial_manager"
                                    value={formData.financial_manager}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    placeholder="(99) 9 9999-9999"
                                    required
                                    fullWidth
                                    label="Número de Whatsapp"
                                    name="financial_manager_contact"
                                    value={formData.financial_manager_contact}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        inputComponent: TextMaskPhone,
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography textAlign={'left'} variant="h4">
                                    Endereço
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    placeholder="99.999-999"
                                    required
                                    fullWidth
                                    label="CEP"
                                    name="zip_code"
                                    value={formData.zip_code}
                                    onChange={handleInputChange}
                                    onBlur={() => formData.zip_code.replace(/\D/g, '').length === 8 && fetchAddressByCep(formData.zip_code)}
                                    InputProps={{
                                        inputComponent: TextMaskCEP,
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Rua"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Número"
                                    name="address_number"
                                    value={formData.address_number}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Complemento"
                                    name="complement"
                                    value={formData.complement}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Bairro"
                                    name="neighborhood"
                                    value={formData.neighborhood}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Cidade"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        label="Estado"
                                        name="state"
                                        value={formData.state}
                                        onChange={handleInputChange}
                                    >
                                        {estados.map((state, index) => (
                                            <MenuItem key={index} value={state.value}>
                                                {state.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography textAlign={'left'} variant="h4">
                                    Documentos
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    id="upload-cnpj"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFileChange(e, setCnpjCertificate)}
                                />
                                <label htmlFor="upload-cnpj">
                                    <Button disabled={isLoading} startIcon={<FaFilePdf />} variant="contained" component="span" fullWidth>
                                        {cnpjCertificate ? cnpjCertificate.name : "Enviar certificado de CNPJ "}
                                    </Button>
                                </label>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    id="upload-address"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFileChange(e, setAddressProof)}
                                />
                                <label htmlFor="upload-address">
                                    <Button disabled={isLoading} startIcon={<FaFilePdf />} variant="contained" component="span" fullWidth>
                                        {addressProof ? addressProof.name : "Enviar comprovante de residência"}
                                    </Button>
                                </label>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    disabled={isLoading}
                                    startIcon={<FaPaperPlane />}
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                >
                                    Solicitar cadastro
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </AppCard>
            </Box>
        </CustomContainer>
    );
};